import { FirebaseTimestamp } from '.';
import { EOrganization } from './organization';
import { ERef } from './firebase';
import { EStripeTransferReversal } from './stripe';

export enum PublisherTransferTrigger {
  /**
   * The publisher's transfer will be attempted within 1 day of the last publication date
   * providing other conditions are also met (see stripe/transfers/validation.ts)
   */
  LastPubDate = 'last-pub-date',

  /**
   * The publisher's transfer will be attempted within 1 day of ad deadline providing
   * other conditions are also met (see stripe/transfers/validation.ts)
   */
  AdDeadline = 'ad-deadline',

  /**
   * The publisher's transfer will likely be attempted on affidavit uploaded date providing
   * other conditions are also met (see stripe/transfers/validation.ts)
   *
   * NOTE: This is currently the default transfer trigger for notices
   */
  AffidavitUpload = 'affidavit-upload'
}

export enum TransferStatus {
  // The transfer will be processed in the near future (e.g. by a cron job)
  PENDING = 'pending',
  // The transfer is currently being processed and locked against duplicate attempts
  IN_PROGRESS = 'in_progress',
  // The transfer does not apply (e.g. zero amount, direct to publisher payment, etc)
  NOT_APPLICABLE = 'not_applicable',
  // The transfer has been successfully completed
  COMPLETED = 'completed',
  // The transfer requires manual review / remediation (e.g. duplicate attempts, partial persistence failure)
  REQUIRES_MANUAL_INTERVENTION = 'requires-manual-intervention'
}

export const DefaultTransferTrigger = PublisherTransferTrigger.AffidavitUpload;

export type ETransfer = {
  id?: string;

  /**
   * In US cents
   */
  amount: number;
  organization: ERef<EOrganization>;
  isCheck?: boolean;

  /**
   * Dwolla Funding Source, see:
   * https://developers.dwolla.com/api-reference/funding-sources
   */
  destination?: string;

  /**
   * Present for notice transfers (absence implies order or other transfer types)
   */
  noticeId?: string;
  status?: string;

  created?: FirebaseTimestamp | Date;
  initiated?: FirebaseTimestamp;

  /**
   * All Stripe transfer reversals associated with this transfer.
   */
  reversalData?: EStripeTransferReversal[];

  /**
   * Aggregate amount (in cents) that has been reversed across all reversal entries.
   */
  amountReversed?: number;

  /**
   * @applicationFeeInvoiceId is the Stripe invoice (created and immediately marked as paid out of band)
   * used to document the reduction in transfer amount to account for any application fees that were
   * deducted from the amount transferred to the publisher (as deemed payment to Column of the app fee by the publisher).
   * The application fee amount is already deducted from the amount on the transfer object.
   */
  applicationFeeInvoiceId?: string;
};

export enum TransferDirectionality {
  TO_PUBLISHER = 'TO_PUBLISHER',
  TO_COLUMN = 'TO_COLUMN'
}

export type NewspaperOrderReversal = {
  newspaperOrderId: string;
  direction: TransferDirectionality;
  /** Whether this reversal represents a partial refund (vs. full cancellation) */
  isPartialRefund: boolean;
  /** Amount in cents being reversed. Required for partial refunds and TO_COLUMN transfers. */
  amountInCents?: number;
  /** Convenience fee portion (in cents) of this refund; used to credit back the fee split on the TO_COLUMN path. */
  convenienceFeePortionInCents?: number;
  /** Transaction id of the staged refund. Required for partial refund idempotency. */
  transactionId?: string;
};
